import { type Static, Type } from "@sinclair/typebox"
import { CardKindSchema } from "./cards"
import { IdSchema, SeatIdSchema, ZoneIdSchema } from "./common"

export const CombatRuntimeStateSchema = Type.Object({
  ignoresGuard: Type.Boolean(), // has Sneak skill
  breaksThrough: Type.Boolean(), // has Breakthrough skill
  hasGuard: Type.Boolean(), // has Guard skill (protects opponent's CC)
  isSwift: Type.Boolean(), // has Swift skill (can attack turn deployed)
  hasArmor: Type.Boolean(), // has Armor skill (reduce incoming damage by 1)
  hasRanged: Type.Boolean(), // has Ranged skill (can attack from support)
  hasCharge: Type.Boolean(), // has Charge skill (support-row start-turn energy)
  hasGenerator: Type.Boolean(), // has Generator skill (boosts Charge output)
  hasFocus: Type.Boolean(), // has Focus skill (attack trigger: gain 1 energy)
  hasKo: Type.Boolean(), // has KO skill (destroy trigger)
  hasDeploy: Type.Boolean(), // has Deploy skill (on-play damage effect)
  hasScrap: Type.Boolean(), // has Scrap skill (self-scrap action)
  hasSpark: Type.Boolean(), // has Spark skill (on-play: gain 1 energy)
  hasCoordinatedStrike: Type.Boolean(), // has Coordinated Strike (support passive: active gets +1 attack)
  hasBattery: Type.Boolean(), // has Battery skill (exhaust: store energy for next turn)
})

export type CombatRuntimeState = Static<typeof CombatRuntimeStateSchema>

export const CardStatusStateSchema = Type.Object({
  exhausted: Type.Boolean(),
  canAttackThisTurn: Type.Boolean(),
  hasAttackedThisTurn: Type.Boolean(),
  deployedThisTurn: Type.Boolean(),
  remainingAttack: Type.Optional(
    Type.Integer({
      minimum: 0,
    }),
  ),
  jammedForTurnNumber: Type.Optional(
    Type.Integer({
      minimum: 1,
    }),
  ),
})

export type CardStatusState = Static<typeof CardStatusStateSchema>

export const RuntimeModifierStateSchema = Type.Object({
  attackMod: Type.Integer(),
  healthMod: Type.Integer(),
  expiresAfterTurn: Type.Optional(
    Type.Integer({
      minimum: 1,
    }),
  ),
  expiresAfterSeatId: Type.Optional(SeatIdSchema),
})

export type RuntimeModifierState = Static<typeof RuntimeModifierStateSchema>

export const CardMemoryStateSchema = Type.Object({
  koCount: Type.Integer({
    minimum: 0,
  }),
  timesActivated: Type.Integer({
    minimum: 0,
  }),
})

export type CardMemoryState = Static<typeof CardMemoryStateSchema>

export const CardInstanceStateSchema = Type.Object({
  instanceId: IdSchema,
  definitionId: IdSchema,
  kind: CardKindSchema,
  recruitCost: Type.Optional(
    Type.Integer({
      minimum: 0,
    }),
  ),
  ownerSeatId: SeatIdSchema,
  zone: ZoneIdSchema,
  currentAttack: Type.Integer({
    minimum: 0,
  }),
  currentHealth: Type.Integer({
    minimum: 0,
  }),
  maxHealth: Type.Integer({
    minimum: 1,
  }),
  combatState: CombatRuntimeStateSchema,
  status: CardStatusStateSchema,
  modifiers: Type.Array(RuntimeModifierStateSchema),
  memory: CardMemoryStateSchema,
  pendingBatteryEnergy: Type.Optional(
    Type.Integer({
      minimum: 1,
    }),
  ),
})

export type CardInstanceState = Static<typeof CardInstanceStateSchema>
